import React, { useState } from 'react';
import SideNav from '../components/SideNav';
import { Mic, Search, Target, AlertTriangle, CheckCircle, BarChart3, TrendingUp, Users, DollarSign, Activity } from 'lucide-react';

const ViabilityLab = () => {
  const [step, setStep] = useState(2); // 0=Input, 1=Analyzing, 2=Results (Set to 2 for Demo)
  const [targetGroup, setTargetGroup] = useState('Mobile Mechanics for Classic Cars');

  // MOCK DATA FOR GOOGLE DEMO
  const mockPainPoints = [
    { id: 1, text: "Parts availability for pre-1980 models is unreliable.", score: 92 },
    { id: 2, text: "Customers distrust quotes due to lack of transparency.", score: 88 },
    { id: 3, text: "Scheduling conflicts with 9-5 shop hours.", score: 85 },
    { id: 4, text: "Towing costs for non-running classics are prohibitive.", score: 81 },
    { id: 5, text: "Lack of specialized knowledge in general repair shops.", score: 78 },
  ];

  const mockCompetitors = [
    { name: "Hagerty Drivers Club", type: "Insurance/Roadside", threat: "LOW" },
    { name: "Local General Shops", type: "Brick & Mortar", threat: "MEDIUM" },
    { name: "YourMechanic.com", type: "Gig App", threat: "HIGH" },
  ];

  return (
    <div style={styles.layout}>
      <SideNav activePage="/viability-lab" />

      <div style={styles.mainContent}>
        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>VIABILITY LAB <span style={styles.betaTag}>ALPHA PREVIEW</span></h1>
            <p style={styles.subTitle}>MARKET GAP ANALYSIS // "GOLDILOCKS ZONE" DETECTOR</p>
          </div>
          <div style={styles.statusBadge}>
            <Activity size={16} /> ANALYSIS COMPLETE
          </div>
        </div>

        <div style={styles.gridContainer}>

          {/* LEFT: INPUTS */}
          <div style={styles.inputPanel}>
            <div style={styles.sectionTitle}>1. TARGET GROUP</div>
            <div style={styles.inputWrapper}>
              <input
                style={styles.input}
                value={targetGroup}
                onChange={(e) => setTargetGroup(e.target.value)}
              />
              <Mic size={20} color="#D4AF37" style={{ cursor: 'pointer' }} />
            </div>

            <div style={styles.sectionTitle}>2. IDENTIFIED PAIN POINTS</div>
            <div style={styles.listContainer}>
              {mockPainPoints.map((pp, i) => (
                <div key={pp.id} style={styles.listItem}>
                  <div style={styles.rankNum}>{i + 1}</div>
                  <div style={styles.listText}>{pp.text}</div>
                  <div style={styles.scoreBadge}>{pp.score}</div>
                </div>
              ))}
            </div>

            <div style={styles.sectionTitle}>3. EXISTING SOLUTIONS</div>
            <div style={styles.listContainer}>
              {mockCompetitors.map((comp, i) => (
                <div key={i} style={styles.compItem}>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{comp.name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#666' }}>{comp.type}</div>
                  </div>
                  <div style={{
                    ...styles.threatBadge,
                    color: comp.threat === 'HIGH' ? '#ff3333' : comp.threat === 'MEDIUM' ? 'orange' : '#4caf50',
                    border: `1px solid ${comp.threat === 'HIGH' ? '#ff3333' : comp.threat === 'MEDIUM' ? 'orange' : '#4caf50'}`
                  }}>{comp.threat} THREAT</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: VIABILITY SCORECARD (THE "GOLDILOCKS ZONE") */}
          <div style={styles.resultPanel}>
            <div style={styles.scoreHeader}>
              <Target size={24} color="#D4AF37" />
              <h2>MILLION VIABILITY SCORE</h2>
            </div>

            <div style={styles.bigScore}>
              87<span style={{ fontSize: '1.5rem', color: '#666' }}>/100</span>
            </div>
            <div style={styles.zoneLabel}>STATUS: <span style={{ color: '#4caf50' }}>GOLDILOCKS ZONE CONFIRMED</span></div>

            <div style={styles.metricsGrid}>
              <div style={styles.metricCard}>
                <TrendingUp size={24} color="#4caf50" />
                <h3>DEMAND</h3>
                <p>HIGH (Rising)</p>
              </div>
              <div style={styles.metricCard}>
                <Users size={24} color="#D4AF37" />
                <h3>NICHE SIZE</h3>
                <p>OPTIMAL</p>
              </div>
              <div style={styles.metricCard}>
                <DollarSign size={24} color="#2196f3" />
                <h3>PURCHASE POWER</h3>
                <p>VERY HIGH</p>
              </div>
              <div style={styles.metricCard}>
                <AlertTriangle size={24} color="orange" />
                <h3>SATURATION</h3>
                <p>LOW</p>
              </div>
            </div>

            <div style={styles.aiSummary}>
              <h3><Search size={16} /> MILLION ANALYSIS:</h3>
              <p>
                "This market is ripe for disruption. Classic car owners have high disposable income but currently face long wait times at general shops.
                By offering <strong>'Concierge Mobile Repair'</strong> specifically for vintage engines, you solve the 'Towing' pain point (Rank #4) and the 'Trust' pain point (Rank #2) simultaneously.
                <strong>Verdict: PROCEED TO SALES LAB.</strong>"
              </p>
            </div>

            <button style={styles.actionBtn}>GENERATE BUSINESS PLAN</button>

          </div>

        </div>
      </div>
    </div>
  );
};

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#000', color: '#fff', fontFamily: 'Inter, sans-serif' },
  mainContent: { flex: 1, padding: '30px', display: 'flex', flexDirection: 'column' },

  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', borderBottom: '1px solid #222', paddingBottom: '20px' },
  pageTitle: { fontFamily: 'Orbitron', fontSize: '2rem', color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '15px' },
  betaTag: { fontSize: '0.8rem', background: '#222', color: '#D4AF37', padding: '5px 10px', borderRadius: '4px', border: '1px solid #333' },
  subTitle: { color: '#666', margin: '5px 0 0 0', fontSize: '0.9rem', letterSpacing: '1px' },
  statusBadge: { display: 'flex', alignItems: 'center', gap: '8px', color: '#4caf50', fontSize: '0.9rem', fontFamily: 'Orbitron' },

  gridContainer: { display: 'flex', gap: '30px', flex: 1 },

  // INPUT PANEL (LEFT)
  inputPanel: { flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' },
  sectionTitle: { fontFamily: 'Orbitron', color: '#D4AF37', fontSize: '0.9rem', marginBottom: '10px' },
  inputWrapper: { display: 'flex', alignItems: 'center', background: '#111', border: '1px solid #333', padding: '10px', borderRadius: '4px' },
  input: { background: 'transparent', border: 'none', color: '#fff', flex: 1, fontSize: '1rem', outline: 'none' },

  listContainer: { display: 'flex', flexDirection: 'column', gap: '10px' },
  listItem: { display: 'flex', alignItems: 'center', gap: '10px', background: '#0a0a0a', padding: '10px', border: '1px solid #222', borderRadius: '4px' },
  rankNum: { fontFamily: 'Orbitron', color: '#666', width: '20px' },
  listText: { flex: 1, fontSize: '0.9rem' },
  scoreBadge: { background: '#222', color: '#D4AF37', padding: '2px 6px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold' },

  compItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0a0a0a', padding: '10px', border: '1px solid #222', borderRadius: '4px' },
  threatBadge: { fontSize: '0.7rem', padding: '2px 6px', borderRadius: '4px' },

  // RESULT PANEL (RIGHT)
  resultPanel: { flex: 1, background: '#111', border: '1px solid #333', borderRadius: '8px', padding: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  scoreHeader: { display: 'flex', alignItems: 'center', gap: '10px', fontFamily: 'Orbitron', color: '#fff' },
  bigScore: { fontSize: '5rem', fontFamily: 'Orbitron', color: '#D4AF37', lineHeight: '1' },
  zoneLabel: { fontFamily: 'Orbitron', fontSize: '1.2rem', marginBottom: '30px' },

  metricsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', width: '100%', marginBottom: '30px' },
  metricCard: { background: '#0a0a0a', border: '1px solid #333', padding: '15px', borderRadius: '4px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' },

  aiSummary: { background: 'rgba(212, 175, 55, 0.1)', border: '1px solid #D4AF37', padding: '20px', borderRadius: '8px', marginBottom: '20px', width: '100%' },

  actionBtn: { width: '100%', background: '#D4AF37', color: '#000', border: 'none', padding: '15px', fontSize: '1rem', fontWeight: 'bold', fontFamily: 'Orbitron', cursor: 'pointer' }
};

export default ViabilityLab;